import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
    UserIcon,
    PhoneIcon,
    MapPinIcon,
    ClockIcon,
    ChatBubbleLeftIcon,
    PlusIcon,
    CalendarIcon,
    CheckCircleIcon,
    PencilIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import {
    FlagIcon as FlagIconSolid,
    CalendarIcon as CalendarIconSolid,
} from '@heroicons/react/24/solid';
import { useDispatch } from 'react-redux';
import {
    addNoteToUser,
    deleteNoteFromUser,
    updateUserPriority,
    updateLastContacted,
    updateAppointmentStatus,
    deleteAppointment,
    updateUserStage,
    CRM_STAGES,
    STAGE_CONFIG,
    updateUserDetails,
} from '../../redux/slices/crmSlice';
import { format, formatDistanceToNow } from 'date-fns';
import NotesList from './NotesList';
import AppointmentModal from './AppointmentModal';
import ServicesModal from './ServicesModal';
import EditUserModal from './EditUserModal';
import CustomDropdown from '../common/CustomDropdown';
import toast from 'react-hot-toast';

const PRIORITY_COLORS = {
    low: 'text-green-600',
    medium: 'text-yellow-600',
    high: 'text-red-600',
};

const SERVICE_LABELS = {
    weightgain: 'Weight Gain',
    weightloss: 'Weight Loss',
    childnutrition: 'Child Nutrition',
    generalhealth: 'General Health',
};

const UserCard = ({ user, handleDragStart }) => {
    const dispatch = useDispatch();
    const [showNotes, setShowNotes] = useState(false);
    const [showAddNote, setShowAddNote] = useState(false);
    const [showAppointments, setShowAppointments] = useState(false);
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [showServicesModal, setShowServicesModal] = useState(false);
    const [showEditUserModal, setShowEditUserModal] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingAppointment, setEditingAppointment] = useState(null);

    const handleAddNote = async (e) => {
        e.preventDefault();
        if (!noteText.trim()) return;

        setIsSubmitting(true);
        try {
            await dispatch(
                addNoteToUser({
                    userId: user.id,
                    noteText: noteText.trim(),
                }),
            ).unwrap();

            setNoteText('');
            setShowAddNote(false);
        } catch (error) {
            console.error('Error adding note:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteNote = async (noteId) => {
        try {
            await dispatch(deleteNoteFromUser({ userId: user.id, noteId })).unwrap();
        } catch (error) {
            console.error('Error deleting note:', error);
        }
    };

    const handlePriorityChange = async (newPriority) => {
        try {
            await dispatch(
                updateUserPriority({
                    userId: user.id,
                    priority: newPriority,
                }),
            ).unwrap();
        } catch (error) {
            console.error('Error updating priority:', error);
        }
    };

    const handleMarkContacted = async () => {
        try {
            await dispatch(updateLastContacted({ userId: user.id })).unwrap();
        } catch (error) {
            console.error('Error marking as contacted:', error);
        }
    };

    const handleCompleteAppointment = async (appointmentId) => {
        try {
            await dispatch(
                updateAppointmentStatus({
                    userId: user.id,
                    appointmentId,
                    status: 'completed',
                }),
            ).unwrap();
        } catch (error) {
            console.error('Error completing appointment:', error);
        }
    };

    const handleDeleteAppointment = async (appointmentId) => {
        if (window.confirm('Are you sure you want to delete this appointment?')) {
            try {
                await dispatch(
                    deleteAppointment({
                        userId: user.id,
                        appointmentId,
                    }),
                ).unwrap();
                toast.success('Appointment deleted successfully!');
            } catch (error) {
                console.error('Error deleting appointment:', error);
                toast.error('Failed to delete appointment');
            }
        }
    };

    const handleEditAppointment = (appointment) => {
        setEditingAppointment(appointment);
        setShowAppointmentModal(true);
    };

    const handleStageChange = async (newStage) => {
        try {
            await dispatch(
                updateUserStage({
                    userId: user.id,
                    newStage,
                }),
            ).unwrap();
            toast.success('Stage updated successfully!');
        } catch (error) {
            console.error('Error updating stage:', error);
            toast.error('Failed to update stage');
        }
    };

    const handleEditUser = async (userId, userData) => {
        try {
            await dispatch(updateUserDetails({ userId, userData })).unwrap();
            toast.success('User details updated successfully!');
        } catch (error) {
            console.error('Error updating user details:', error);
            toast.error('Failed to update user details');
        }
    };

    const sortedNotes = user.notes
        ? [...user.notes].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        : [];

    const sortedAppointments = user.appointments
        ? [...user.appointments]
            .filter((apt) => apt.status === 'scheduled')
            .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime))
        : [];

    const upcomingAppointment = sortedAppointments[0];

    // Prepare stage options for CustomDropdown
    const stageOptions = Object.keys(CRM_STAGES).map((stageKey) => {
        const stage = CRM_STAGES[stageKey];
        const config = STAGE_CONFIG[stage];

        // Extract color name from Tailwind class (e.g., 'text-blue-600' -> 'blue')
        const colorMatch = config.color.match(/text-(\w+)-/);
        const color = colorMatch ? colorMatch[1] : 'gray';

        return {
            value: stage,
            label: config.title,
            color: color,
        };
    });

    return (
        <motion.div
            layout
            layoutId={user.id}
            draggable='true'
            onDragStart={(e) => handleDragStart(e, user)}
            className='cursor-grab rounded-xl border border-slate-200 bg-white p-4 active:cursor-grabbing hover:border-slate-300 hover:shadow-lg transition-all shadow-sm'
        >
            {/* Header with Priority */}
            <div className='flex items-start justify-between mb-3'>
                <div className='flex items-center gap-2 flex-wrap'>
                    <div className='w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0'>
                        <UserIcon className='h-4 w-4 text-slate-600' />
                    </div>
                    <h4 className='font-black text-slate-900'>{user.name}</h4>
                    {/* Lead Type Tag */}
                    {user.leadType && (
                        <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest ${user.leadType === 'business'
                                ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                : 'bg-blue-100 text-blue-700 border border-blue-200'
                                }`}
                        >
                            {user.leadType === 'business' ? 'Business' : 'Service'}
                        </span>
                    )}
                </div>
                <div className='relative group'>
                    <button
                        className={`${PRIORITY_COLORS[user.priority || 'medium']
                            } hover:scale-110 transition-transform`}
                    >
                        <FlagIconSolid className='h-5 w-5' />
                    </button>
                    {/* Priority Dropdown */}
                    <div className='hidden group-hover:block absolute right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-10 overflow-hidden'>
                        <button
                            onClick={() => handlePriorityChange('high')}
                            className='block w-full px-3 py-2 text-left text-red-600 hover:bg-slate-50 text-xs font-medium'
                        >
                            High Priority
                        </button>
                        <button
                            onClick={() => handlePriorityChange('medium')}
                            className='block w-full px-3 py-2 text-left text-yellow-600 hover:bg-slate-50 text-xs font-medium'
                        >
                            Medium Priority
                        </button>
                        <button
                            onClick={() => handlePriorityChange('low')}
                            className='block w-full px-3 py-2 text-left text-green-600 hover:bg-slate-50 text-xs font-medium'
                        >
                            Low Priority
                        </button>
                    </div>
                </div>
                {/* Edit Button */}
                <button
                    onClick={() => setShowEditUserModal(true)}
                    className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-blue-600"
                    title="Edit User Details"
                >
                    <PencilIcon className="h-4 w-4" />
                </button>
            </div>


            {/* Mobile-only Stage Selector */}
            <div className='md:hidden mb-3'>
                <label className='block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2'>Change Stage</label>
                <CustomDropdown
                    value={user.stage}
                    onChange={handleStageChange}
                    options={stageOptions}
                    placeholder='Select stage'
                />
                <p className='text-xs text-slate-500 font-medium mt-2'>
                    {STAGE_CONFIG[user.stage]?.description}
                </p>
            </div>

            {/* Contact Info */}
            <div className='space-y-2 mb-3'>
                <div className='flex items-center gap-3'>
                    <div className='w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center flex-shrink-0'>
                        <PhoneIcon className='h-4 w-4 text-slate-400' />
                    </div>
                    <span className='text-sm font-semibold text-slate-900'>{user.number}</span>
                </div>

                {/* Show different fields based on lead type */}
                {user.leadType === 'business' ? (
                    <>
                        {/* Business Lead Fields */}
                        {user.location && (
                            <div className='flex items-start gap-3'>
                                <div className='w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5'>
                                    <MapPinIcon className='h-4 w-4 text-slate-400' />
                                </div>
                                <span className='text-sm text-slate-700 break-words'>{user.location}</span>
                            </div>
                        )}
                        {user.current_working && (
                            <div className='flex items-start gap-3'>
                                <div className='w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5'>
                                    <UserIcon className='h-4 w-4 text-slate-400' />
                                </div>
                                <span className='text-sm text-slate-700 break-words'>Working: {user.current_working}</span>
                            </div>
                        )}
                        {user.is_existing_business === 1 && user.business_name && (
                            <div className='mt-2'>
                                <span className='text-[10px] font-black text-purple-700 bg-purple-50 px-2 py-1.5 rounded-lg border border-purple-200 uppercase tracking-widest inline-block'>
                                    {user.business_name} ({user.established_year})
                                </span>
                            </div>
                        )}
                        {user.age && (
                            <div className='text-xs text-slate-500 font-medium'>
                                Age: {user.age}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {/* Service Lead Fields */}
                        {user.address && (
                            <div className='flex items-start gap-3'>
                                <div className='w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5'>
                                    <MapPinIcon className='h-4 w-4 text-slate-400' />
                                </div>
                                <span className='text-sm text-slate-700 break-words'>{user.address}</span>
                            </div>
                        )}
                        <div className='flex flex-wrap gap-2 text-xs text-slate-500 font-medium'>
                            {user.age && <span>Age: {user.age}</span>}
                            {user.weight && <span>Weight: {user.weight}</span>}
                            {user.gender && <span>Gender: {user.gender}</span>}
                        </div>
                    </>
                )}
            </div>

            {/* Services Section - Only for Service Leads */}
            {
                user.leadType !== 'business' && (
                    <div className='mb-3 border-t border-slate-100 pt-3'>
                        <div className='flex items-center justify-between mb-2'>
                            <span className='text-[9px] font-black text-slate-400 uppercase tracking-widest'>Services</span>
                            <button
                                onClick={() => setShowServicesModal(true)}
                                className='p-1 rounded-lg hover:bg-slate-100 transition-colors'
                                title='Edit services'
                            >
                                <PencilIcon className='h-3 w-3 text-slate-500 hover:text-slate-900' />
                            </button>
                        </div>
                        <div className='flex flex-wrap gap-1.5'>
                            {(() => {
                                // Handle both string and array formats
                                const services = Array.isArray(user.services)
                                    ? user.services
                                    : [user.services];
                                return services.filter(Boolean).map((service, index) => (
                                    <span
                                        key={index}
                                        className='inline-block px-2 py-1 text-[9px] font-black uppercase tracking-widest rounded-lg bg-blue-50 text-blue-700 border border-blue-200'
                                    >
                                        {SERVICE_LABELS[service] || service}
                                    </span>
                                ));
                            })()}
                            {(!user.services ||
                                (Array.isArray(user.services) && user.services.length === 0)) && (
                                    <span className='text-xs text-slate-400 italic'>No services selected</span>
                                )}
                        </div>
                    </div>
                )
            }

            {/* Timestamps */}
            <div className='space-y-1 mb-3 text-[10px] text-slate-500 font-medium border-t border-slate-100 pt-3'>
                {user.createdAt && (
                    <div className='flex items-center gap-1'>
                        <CalendarIcon className='h-3 w-3' />
                        <span>
                            Added:{' '}
                            {(() => {
                                try {
                                    let date;
                                    if (user.createdAt?.seconds) {
                                        date = new Date(user.createdAt.seconds * 1000);
                                    } else if (typeof user.createdAt === 'string') {
                                        date = new Date(user.createdAt);
                                    } else if (user.createdAt instanceof Date) {
                                        date = user.createdAt;
                                    } else {
                                        return 'recently';
                                    }

                                    // Check if date is valid
                                    if (isNaN(date.getTime())) return 'recently';

                                    return formatDistanceToNow(date, { addSuffix: true });
                                } catch (e) {
                                    return 'recently';
                                }
                            })()}
                        </span>
                    </div>
                )}
                {user.lastContactedAt && (
                    <div className='flex items-center gap-1'>
                        <ClockIcon className='h-3 w-3' />
                        <span>
                            Last contact:{' '}
                            {(() => {
                                try {
                                    let date;
                                    if (user.lastContactedAt?.seconds) {
                                        date = new Date(user.lastContactedAt.seconds * 1000);
                                    } else if (typeof user.lastContactedAt === 'string') {
                                        date = new Date(user.lastContactedAt);
                                    } else if (user.lastContactedAt instanceof Date) {
                                        date = user.lastContactedAt;
                                    } else {
                                        return 'recently';
                                    }

                                    if (isNaN(date.getTime())) return 'recently';

                                    return formatDistanceToNow(date, { addSuffix: true });
                                } catch (e) {
                                    return 'recently';
                                }
                            })()}
                        </span>
                    </div>
                )}
            </div>

            {/* Upcoming Appointment Highlight */}
            {
                upcomingAppointment && (
                    <div className='mb-3 p-3 rounded-xl bg-green-50 border border-green-200'>
                        <div className='flex items-start justify-between'>
                            <div className='flex-1'>
                                <div className='flex items-center gap-2 mb-1'>
                                    <CalendarIconSolid className='h-4 w-4 text-green-600' />
                                    <span className='text-[9px] font-black text-green-700 uppercase tracking-widest'>
                                        Upcoming Appointment
                                    </span>
                                </div>
                                <p className='text-sm text-slate-900 font-black'>
                                    {upcomingAppointment.title}
                                </p>
                                <p className='text-xs text-slate-600 font-medium mt-1'>
                                    {format(
                                        new Date(upcomingAppointment.dateTime),
                                        'MMM dd, yyyy • hh:mm a',
                                    )}
                                </p>
                            </div>
                            <button
                                onClick={() => handleCompleteAppointment(upcomingAppointment.id)}
                                className='p-1.5 rounded-lg hover:bg-green-100 transition-colors'
                                title='Mark as completed'
                            >
                                <CheckCircleIcon className='h-5 w-5 text-green-600' />
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Appointments Section */}
            <div className='border-t border-slate-100 pt-3 mb-3'>
                <div className='flex items-center justify-between mb-2'>
                    <button
                        onClick={() => setShowAppointments(!showAppointments)}
                        className='flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-900 transition-colors'
                    >
                        <CalendarIcon className='h-4 w-4' />
                        <span>Appointments ({sortedAppointments.length})</span>
                    </button>
                    <button
                        onClick={() => setShowAppointmentModal(true)}
                        className='p-1 rounded-lg hover:bg-slate-100 transition-colors'
                    >
                        <PlusIcon className='h-4 w-4 text-slate-500 hover:text-slate-900' />
                    </button>
                </div>

                {/* Appointments List */}
                {showAppointments && sortedAppointments.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className='space-y-2 mb-3'
                    >
                        {sortedAppointments.map((apt) => (
                            <div
                                key={apt.id}
                                className='bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs'
                            >
                                <div className='flex items-start justify-between gap-2'>
                                    <div className='flex-1'>
                                        <p className='text-slate-900 font-black'>{apt.title}</p>
                                        {apt.description && (
                                            <p className='text-slate-600 font-medium mt-1'>{apt.description}</p>
                                        )}
                                        <p className='text-slate-500 font-medium mt-1'>
                                            {format(new Date(apt.dateTime), 'MMM dd • hh:mm a')}
                                        </p>
                                    </div>
                                    <div className='flex items-center gap-1'>
                                        <button
                                            onClick={() => handleEditAppointment(apt)}
                                            className='p-1 rounded-lg hover:bg-slate-200 transition-colors'
                                            title='Edit appointment'
                                        >
                                            <PencilIcon className='h-4 w-4 text-slate-500 hover:text-blue-600' />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteAppointment(apt.id)}
                                            className='p-1 rounded-lg hover:bg-slate-200 transition-colors'
                                            title='Delete appointment'
                                        >
                                            <TrashIcon className='h-4 w-4 text-slate-500 hover:text-red-600' />
                                        </button>
                                        <button
                                            onClick={() => handleCompleteAppointment(apt.id)}
                                            className='p-1 rounded-lg hover:bg-slate-200 transition-colors'
                                            title='Mark as completed'
                                        >
                                            <CheckCircleIcon className='h-4 w-4 text-slate-500 hover:text-green-600' />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </motion.div>
                )}

                {showAppointments && sortedAppointments.length === 0 && (
                    <p className='text-xs text-slate-500 text-center py-2 font-medium'>
                        No scheduled appointments
                    </p>
                )}
            </div>

            {/* Notes Section */}
            <div className='border-t border-slate-100 pt-3'>
                <div className='flex items-center justify-between mb-2'>
                    <button
                        onClick={() => setShowNotes(!showNotes)}
                        className='flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-900 transition-colors'
                    >
                        <ChatBubbleLeftIcon className='h-4 w-4' />
                        <span>Notes ({sortedNotes.length})</span>
                    </button>
                    <button
                        onClick={() => setShowAddNote(!showAddNote)}
                        className='p-1 rounded-lg hover:bg-slate-100 transition-colors'
                    >
                        <PlusIcon className='h-4 w-4 text-slate-500 hover:text-slate-900' />
                    </button>
                </div>

                {/* Add Note Form */}
                {showAddNote && (
                    <motion.form
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        onSubmit={handleAddNote}
                        className='mb-2'
                    >
                        <textarea
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder='Add a note...'
                            className='w-full p-3 text-sm rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-medium'
                            rows='2'
                            autoFocus
                        />
                        <div className='flex items-center justify-end gap-2 mt-2'>
                            <button
                                type='button'
                                onClick={() => {
                                    setShowAddNote(false);
                                    setNoteText('');
                                }}
                                className='px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:text-slate-900 transition-colors'
                            >
                                Cancel
                            </button>
                            <button
                                type='submit'
                                disabled={isSubmitting || !noteText.trim()}
                                className='px-3 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                                {isSubmitting ? 'Adding...' : 'Add Note'}
                            </button>
                        </div>
                    </motion.form>
                )}

                {/* Notes List */}
                {showNotes && <NotesList notes={sortedNotes} onDeleteNote={handleDeleteNote} />}
            </div>

            {/* Actions */}
            <div className='border-t border-slate-100 pt-3 mt-3'>
                {(() => {
                    // Check if contacted within last 24 hours
                    const isRecentlyContacted =
                        user.lastContactedAt &&
                        new Date() - new Date(user.lastContactedAt) < 24 * 60 * 60 * 1000;

                    return (
                        <button
                            onClick={handleMarkContacted}
                            className={`w-full px-3 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 ${isRecentlyContacted
                                ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'
                                }`}
                        >
                            <ClockIcon className='h-4 w-4' />
                            {isRecentlyContacted ? 'Recently Contacted' : 'Mark as Contacted'}
                        </button>
                    );
                })()}
            </div>

            {/* Appointment Modal - Rendered via Portal */}
            {createPortal(
                <AppointmentModal
                    isOpen={showAppointmentModal}
                    onClose={() => {
                        setShowAppointmentModal(false);
                        setEditingAppointment(null);
                    }}
                    user={user}
                    editingAppointment={editingAppointment}
                />,
                document.body
            )}

            {/* Services Modal - Rendered via Portal */}
            {createPortal(
                <ServicesModal
                    isOpen={showServicesModal}
                    onClose={() => setShowServicesModal(false)}
                    user={user}
                />,
                document.body
            )}

            {/* Edit User Modal - Rendered via Portal */}
            {createPortal(
                <EditUserModal
                    isOpen={showEditUserModal}
                    onClose={() => setShowEditUserModal(false)}
                    onSubmit={handleEditUser}
                    user={user}
                />,
                document.body
            )}
        </motion.div>
    );
};

export default UserCard;
